import { useEffect, useRef, useState } from 'react';

/**
 * Scroll-reveal hook for the redesign.
 *
 * Returns [ref, isRevealed]. Attach the ref to any element styled with the
 * `oli-reveal`, `oli-reveal-memorial`, or `oli-stagger` classes from
 * src/theme.css and append `is-revealed` when the flag flips true:
 *
 *   const [ref, revealed] = useReveal();
 *   <section ref={ref} className={`oli-reveal ${revealed ? 'is-revealed' : ''}`}>
 *
 * Reduced-motion users see content immediately (theme.css neutralizes the
 * hidden state), so this hook only ever controls *when* motion plays —
 * never whether content is reachable.
 */
export const useReveal = (options = {}) => {
  const ref = useRef(null);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // No IntersectionObserver (very old browsers / some test envs): just show.
    if (typeof IntersectionObserver === 'undefined') {
      setIsRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsRevealed(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px', ...options }
    );

    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, isRevealed];
};

/** Convenience: className string for a standard reveal block. */
export const revealClass = (revealed, extra = '') =>
  `oli-reveal ${revealed ? 'is-revealed' : ''} ${extra}`.trim();

export default useReveal;
